import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import { Fonts } from '@/constants/theme';
import { fetchTeacherOmrResults, type TeacherOmrResultSheet } from '@/lib/omr-results';
import {
  fetchQuestionsForSet,
  fetchTeacherQuestionSets,
  type QuestionListItem,
  type TeacherQuestionSetListItem,
} from '@/lib/question-sets';
import { useAuth } from '@/providers/auth-provider';

const ALL_CLASSES_ID = '__all_classes__';

type ClassOption = {
  id: string;
  label: string;
};

type PickerType = 'class' | 'set' | null;
type AnalyticsView = 'student' | 'question';
type OptionKey = 'A' | 'B' | 'C' | 'D';

type StudentAttempt = {
  answers: TeacherOmrResultSheet['answers'];
  created_at: string;
  id: string;
  percent: number;
  score: number;
  summary: TeacherOmrResultSheet['summary'];
  total_marks: number;
  uploaded_at: string | null;
};

type StudentAnalyticsItem = {
  attempt_count: number;
  best_attempt: StudentAttempt;
  name: string;
  other_attempts: StudentAttempt[];
  roll: string | null;
};

type QuestionAnalyticsItem = {
  attempts: number;
  blank: number;
  correct: number;
  marked_counts: Record<OptionKey, number>;
  question: QuestionListItem | null;
  question_number: number;
  review: number;
  wrong: number;
};

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

function formatDateTime(value: string | null) {
  if (!value) return 'No time';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function createEmptyMarkedCounts(): Record<OptionKey, number> {
  return { A: 0, B: 0, C: 0, D: 0 };
}

function getOptionText(question: QuestionListItem, option: OptionKey) {
  if (option === 'A') return question.option_a;
  if (option === 'B') return question.option_b;
  if (option === 'C') return question.option_c;
  return question.option_d;
}

function getAnswerStatusLabel(answer: TeacherOmrResultSheet['answers'][number]) {
  if (answer.answer_status === 'blank') return 'Blank';
  if (answer.answer_status === 'multi_marked') return 'Multi marked';
  if (answer.answer_status === 'ambiguous') return 'Ambiguous';
  if (answer.is_correct) return 'Correct';
  return 'Wrong';
}

export default function TeacherResultsScreen() {
  const { profile } = useAuth();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1080;

  const [questionSets, setQuestionSets] = useState<TeacherQuestionSetListItem[]>([]);
  const [results, setResults] = useState<TeacherOmrResultSheet[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<QuestionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<string>(ALL_CLASSES_ID);
  const [selectedSetId, setSelectedSetId] = useState<string>('');
  const [pickerType, setPickerType] = useState<PickerType>(null);
  const [analyticsView, setAnalyticsView] = useState<AnalyticsView>('student');
  const [expandedAttemptId, setExpandedAttemptId] = useState<string | null>(null);
  const [expandedQuestionNumber, setExpandedQuestionNumber] = useState<number | null>(null);

  const loadData = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial') => {
      if (mode === 'initial') {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      if (!profile?.id || !profile.school_id) {
        setQuestionSets([]);
        setResults([]);
        setSelectedQuestions([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        const [setRows, resultRows] = await Promise.all([
          fetchTeacherQuestionSets(profile.id),
          fetchTeacherOmrResults(profile.id, profile.school_id),
        ]);
        setQuestionSets(setRows);
        setResults(resultRows);
      } catch (error) {
        Alert.alert('Unable to load results', getErrorMessage(error));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [profile?.id, profile?.school_id]
  );

  useEffect(() => {
    void loadData('initial');
  }, [loadData]);

  const classOptions = useMemo(() => {
    const items = questionSets.reduce<ClassOption[]>((acc, set) => {
      if (acc.some((item) => item.id === set.class_id)) return acc;
      acc.push({ id: set.class_id, label: set.class_label });
      return acc;
    }, []);

    return [{ id: ALL_CLASSES_ID, label: 'All classes' }, ...items];
  }, [questionSets]);

  useEffect(() => {
    if (questionSets.length === 0) {
      setSelectedClassId(ALL_CLASSES_ID);
      setSelectedSetId('');
      return;
    }

    const hasSelectedClass = classOptions.some((option) => option.id === selectedClassId);
    if (!hasSelectedClass || selectedClassId === ALL_CLASSES_ID) {
      setSelectedClassId(classOptions[1]?.id ?? ALL_CLASSES_ID);
    }
  }, [classOptions, questionSets.length, selectedClassId]);

  const visibleSets = useMemo(() => {
    if (selectedClassId === ALL_CLASSES_ID) return questionSets;
    return questionSets.filter((set) => set.class_id === selectedClassId);
  }, [questionSets, selectedClassId]);

  useEffect(() => {
    if (!selectedSetId) return;
    if (!visibleSets.some((set) => set.id === selectedSetId)) {
      setSelectedSetId('');
    }
  }, [selectedSetId, visibleSets]);

  useEffect(() => {
    let isMounted = true;

    const loadQuestions = async () => {
      if (!selectedSetId) {
        setSelectedQuestions([]);
        setExpandedQuestionNumber(null);
        setExpandedAttemptId(null);
        return;
      }

      setQuestionLoading(true);
      setExpandedQuestionNumber(null);
      setExpandedAttemptId(null);

      try {
        const rows = await fetchQuestionsForSet(selectedSetId);
        if (!isMounted) return;
        setSelectedQuestions(rows);
      } catch (error) {
        if (!isMounted) return;
        Alert.alert('Unable to load questions', getErrorMessage(error));
        setSelectedQuestions([]);
      } finally {
        if (isMounted) {
          setQuestionLoading(false);
        }
      }
    };

    void loadQuestions();

    return () => {
      isMounted = false;
    };
  }, [selectedSetId]);

  const selectedClassLabel = useMemo(
    () => classOptions.find((option) => option.id === selectedClassId)?.label ?? 'All classes',
    [classOptions, selectedClassId]
  );

  const selectedSet = useMemo(
    () => visibleSets.find((set) => set.id === selectedSetId) ?? null,
    [selectedSetId, visibleSets]
  );

  const visibleSetIds = useMemo(() => new Set(visibleSets.map((set) => set.id)), [visibleSets]);

  const selectedSetResults = useMemo(() => {
    if (!selectedSetId) return [] as TeacherOmrResultSheet[];
    return results.filter((result) => result.question_set_id === selectedSetId);
  }, [results, selectedSetId]);

  const visibleResultsCount = useMemo(() => {
    const classScopedResults =
      selectedClassId === ALL_CLASSES_ID
        ? results
        : results.filter((result) => visibleSetIds.has(result.question_set_id));

    if (!selectedSetId) return classScopedResults.length;
    return classScopedResults.filter((result) => result.question_set_id === selectedSetId).length;
  }, [results, selectedClassId, selectedSetId, visibleSetIds]);

  const selectedSetAverage = useMemo(() => {
    if (selectedSetResults.length === 0) return 0;
    const total = selectedSetResults.reduce((sum, result) => sum + result.percent, 0);
    return Number((total / selectedSetResults.length).toFixed(1));
  }, [selectedSetResults]);

  const studentAnalytics = useMemo(() => {
    const grouped = new Map<string, StudentAnalyticsItem>();

    for (const result of selectedSetResults) {
      const key = result.student_roll?.trim().toLowerCase() || result.student_name.trim().toLowerCase();
      const attempt: StudentAttempt = {
        answers: result.answers,
        created_at: result.created_at,
        id: result.id,
        percent: result.percent,
        score: result.score,
        summary: result.summary,
        total_marks: result.total_marks,
        uploaded_at: result.uploaded_at,
      };
      const current = grouped.get(key);

      if (!current) {
        grouped.set(key, {
          attempt_count: 1,
          best_attempt: attempt,
          name: result.student_name,
          other_attempts: [],
          roll: result.student_roll,
        });
        continue;
      }

      const attempts = [current.best_attempt, ...current.other_attempts, attempt].sort((a, b) => {
        if (b.percent !== a.percent) return b.percent - a.percent;
        return new Date(b.uploaded_at || b.created_at).getTime() - new Date(a.uploaded_at || a.created_at).getTime();
      });

      grouped.set(key, {
        ...current,
        attempt_count: attempts.length,
        best_attempt: attempts[0],
        other_attempts: attempts.slice(1),
      });
    }

    return Array.from(grouped.values()).sort((a, b) => {
      if (b.best_attempt.percent !== a.best_attempt.percent) {
        return b.best_attempt.percent - a.best_attempt.percent;
      }
      return a.name.localeCompare(b.name);
    });
  }, [selectedSetResults]);

  const questionByNumber = useMemo(
    () => new Map(selectedQuestions.map((question) => [question.position, question] as const)),
    [selectedQuestions]
  );

  const questionAnalytics = useMemo(() => {
    const count = selectedSet?.question_count ?? 0;
    const rows = new Map<number, QuestionAnalyticsItem>();

    for (let index = 1; index <= count; index += 1) {
      rows.set(index, {
        attempts: 0,
        blank: 0,
        correct: 0,
        marked_counts: createEmptyMarkedCounts(),
        question: questionByNumber.get(index) ?? null,
        question_number: index,
        review: 0,
        wrong: 0,
      });
    }

    for (const result of selectedSetResults) {
      for (const answer of result.answers) {
        const current = rows.get(answer.question_number);
        if (!current) continue;

        current.attempts += 1;

        if (
          answer.detected_option === 'A' ||
          answer.detected_option === 'B' ||
          answer.detected_option === 'C' ||
          answer.detected_option === 'D'
        ) {
          current.marked_counts[answer.detected_option] += 1;
        }

        if (answer.answer_status === 'blank') {
          current.blank += 1;
          continue;
        }

        if (answer.is_correct) {
          current.correct += 1;
          continue;
        }

        current.wrong += 1;

        if (answer.answer_status === 'multi_marked' || answer.answer_status === 'ambiguous') {
          current.review += 1;
        }
      }
    }

    return Array.from(rows.values());
  }, [questionByNumber, selectedSet, selectedSetResults]);

  const topStudentScore = studentAnalytics[0]?.best_attempt.percent ?? 0;
  const totalUniqueStudents = studentAnalytics.length;
  const totalQuestionAttempts = useMemo(
    () => questionAnalytics.reduce((sum, item) => sum + item.attempts, 0),
    [questionAnalytics]
  );

  const pickerTitle = pickerType === 'class' ? 'Select class' : 'Select MCQ set';

  if (loading) {
    return (
      <View style={styles.loadingScreen}>
        <ActivityIndicator size="large" color="#1E5E3D" />
        <Text style={styles.loadingText}>Loading saved results and MCQ sets...</Text>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ScrollView
        contentContainerStyle={[styles.content, isDesktop && styles.contentDesktop]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => void loadData('refresh')}
            tintColor="#1E5E3D"
          />
        }
        showsVerticalScrollIndicator={false}>
        <View style={styles.hero}>
          <Text style={styles.heroEyebrow}>Teacher results</Text>
          <Text style={styles.heroTitle}>Review scanned performance by class</Text>
          <Text style={styles.heroSubtitle}>
            Choose a class first, then choose the matching MCQ set. After that, switch between
            student-wise and question-wise analytics.
          </Text>

          <View style={styles.heroStats}>
            <View style={styles.heroStatCard}>
              <Text style={styles.heroStatValue}>{classOptions.length - 1}</Text>
              <Text style={styles.heroStatLabel}>Classes</Text>
            </View>
            <View style={styles.heroStatCard}>
              <Text style={styles.heroStatValue}>{visibleSets.length}</Text>
              <Text style={styles.heroStatLabel}>MCQ sets</Text>
            </View>
            <View style={styles.heroStatCard}>
              <Text style={styles.heroStatValue}>{visibleResultsCount}</Text>
              <Text style={styles.heroStatLabel}>Saved scans</Text>
            </View>
          </View>
        </View>

        {questionSets.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialIcons name="layers-clear" size={42} color="#97A397" />
            <Text style={styles.emptyTitle}>No MCQ sets found</Text>
            <Text style={styles.emptyText}>
              Create a question set first. Saved results will appear here after scans are processed.
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Selectors</Text>
                <Text style={styles.sectionCaption}>
                  {selectedSet ? selectedSet.title : selectedClassLabel}
                </Text>
              </View>

              <View style={styles.selectorStack}>
                <PickerField
                  icon="groups"
                  label="Class"
                  onPress={() => setPickerType('class')}
                  value={selectedClassLabel}
                />

                <PickerField
                  icon="library-books"
                  label="MCQ set"
                  onPress={() => {
                    if (visibleSets.length === 0) return;
                    setPickerType('set');
                  }}
                  value={selectedSet?.title ?? 'Select MCQ set'}
                  disabled={visibleSets.length === 0}
                />
              </View>

              {visibleSets.length === 0 ? (
                <View style={styles.compactEmptyState}>
                  <Text style={styles.compactEmptyTitle}>No MCQ sets in this class</Text>
                  <Text style={styles.compactEmptyText}>
                    Pick another class or create a set for this class first.
                  </Text>
                </View>
              ) : selectedSet ? (
                <View style={styles.selectionCard}>
                  <Text style={styles.selectionLabel}>Selected MCQ set</Text>
                  <Text style={styles.selectionTitle}>{selectedSet.title}</Text>
                  <Text style={styles.selectionMeta}>
                    {selectedSet.subject_label} | {selectedSet.question_count} questions
                  </Text>
                  <View style={styles.selectionStats}>
                    <View style={styles.selectionStatPill}>
                      <Text style={styles.selectionStatValue}>{selectedClassLabel}</Text>
                      <Text style={styles.selectionStatLabel}>class</Text>
                    </View>
                    <View style={styles.selectionStatPill}>
                      <Text style={styles.selectionStatValue}>{selectedSetResults.length}</Text>
                      <Text style={styles.selectionStatLabel}>saved scans</Text>
                    </View>
                    <View style={styles.selectionStatPill}>
                      <Text style={styles.selectionStatValue}>
                        {selectedSetResults.length > 0 ? `${selectedSetAverage}%` : '--'}
                      </Text>
                      <Text style={styles.selectionStatLabel}>avg score</Text>
                    </View>
                  </View>
                </View>
              ) : (
                <View style={styles.compactEmptyState}>
                  <Text style={styles.compactEmptyTitle}>Select an MCQ set</Text>
                  <Text style={styles.compactEmptyText}>
                    The MCQ set selector now shows only the sets from {selectedClassLabel}.
                  </Text>
                </View>
              )}
            </View>

            {selectedSet ? (
              <View style={styles.section}>
                <View style={styles.analyticsHero}>
                  <View style={styles.analyticsHeroCopy}>
                    <Text style={styles.analyticsEyebrow}>Actual analytics</Text>
                    <Text style={styles.analyticsTitle}>Performance breakdown</Text>
                    <Text style={styles.analyticsSubtitle}>
                      Student-wise mode now lets you expand every attempt to see question-level
                      answers. Question-wise mode shows option-level marking patterns.
                    </Text>
                  </View>

                  <View style={styles.analyticsMiniStats}>
                    <View style={styles.analyticsMiniStat}>
                      <Text style={styles.analyticsMiniValue}>{totalUniqueStudents}</Text>
                      <Text style={styles.analyticsMiniLabel}>students</Text>
                    </View>
                    <View style={styles.analyticsMiniStat}>
                      <Text style={styles.analyticsMiniValue}>{topStudentScore}%</Text>
                      <Text style={styles.analyticsMiniLabel}>top score</Text>
                    </View>
                    <View style={styles.analyticsMiniStat}>
                      <Text style={styles.analyticsMiniValue}>{totalQuestionAttempts}</Text>
                      <Text style={styles.analyticsMiniLabel}>answers</Text>
                    </View>
                  </View>
                </View>

                <View style={styles.analyticsToggleShell}>
                  <Pressable
                    onPress={() => setAnalyticsView('student')}
                    style={[
                      styles.analyticsToggleButton,
                      analyticsView === 'student' && styles.analyticsToggleButtonActive,
                    ]}>
                    <MaterialIcons
                      name="groups"
                      size={18}
                      color={analyticsView === 'student' ? '#FFFFFF' : '#215136'}
                    />
                    <Text
                      style={[
                        styles.analyticsToggleText,
                        analyticsView === 'student' && styles.analyticsToggleTextActive,
                      ]}>
                      Student wise
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={() => setAnalyticsView('question')}
                    style={[
                      styles.analyticsToggleButton,
                      analyticsView === 'question' && styles.analyticsToggleButtonActive,
                    ]}>
                    <MaterialIcons
                      name="quiz"
                      size={18}
                      color={analyticsView === 'question' ? '#FFFFFF' : '#215136'}
                    />
                    <Text
                      style={[
                        styles.analyticsToggleText,
                        analyticsView === 'question' && styles.analyticsToggleTextActive,
                      ]}>
                      Question wise
                    </Text>
                  </Pressable>
                </View>

                {selectedSetResults.length === 0 ? (
                  <View style={styles.compactEmptyState}>
                    <Text style={styles.compactEmptyTitle}>No analytics yet</Text>
                    <Text style={styles.compactEmptyText}>
                      This MCQ set has no saved scans yet, so there is nothing to analyze.
                    </Text>
                  </View>
                ) : analyticsView === 'student' ? (
                  <View style={styles.analyticsList}>
                    {studentAnalytics.map((student, index) => (
                      <View key={`${student.roll ?? student.name}`} style={styles.analyticsCard}>
                        <View style={styles.analyticsCardHeader}>
                          <View style={styles.analyticsRankBadge}>
                            <Text style={styles.analyticsRankText}>#{index + 1}</Text>
                          </View>

                          <View style={styles.analyticsHeaderCopy}>
                            <Text style={styles.analyticsCardTitle}>{student.name}</Text>
                            <Text style={styles.analyticsCardMeta}>
                              {student.roll ? `Roll ${student.roll}` : 'Roll unavailable'} | {student.attempt_count}{' '}
                              attempts
                            </Text>
                          </View>

                          <View style={styles.scoreBadge}>
                            <Text style={styles.scoreBadgeValue}>{student.best_attempt.percent}%</Text>
                            <Text style={styles.scoreBadgeLabel}>best</Text>
                          </View>
                        </View>

                        <View style={styles.metricRow}>
                          <View style={styles.metricPill}>
                            <Text style={styles.metricValue}>
                              {student.best_attempt.score}/{student.best_attempt.total_marks}
                            </Text>
                            <Text style={styles.metricLabel}>highest score</Text>
                          </View>
                          <View style={styles.metricPill}>
                            <Text style={styles.metricValue}>{student.best_attempt.summary.correct}</Text>
                            <Text style={styles.metricLabel}>correct</Text>
                          </View>
                          <View style={styles.metricPill}>
                            <Text style={styles.metricValue}>{student.best_attempt.summary.incorrect}</Text>
                            <Text style={styles.metricLabel}>wrong</Text>
                          </View>
                          <View style={styles.metricPill}>
                            <Text style={styles.metricValue}>{student.best_attempt.summary.blank}</Text>
                            <Text style={styles.metricLabel}>blank</Text>
                          </View>
                        </View>

                        <Pressable
                          onPress={() =>
                            setExpandedAttemptId((current) =>
                              current === student.best_attempt.id ? null : student.best_attempt.id
                            )
                          }
                          style={[styles.attemptDetailCard, styles.attemptDetailCardPrimary]}>
                          <View style={styles.attemptDetailHeader}>
                            <View style={styles.attemptDetailCopy}>
                              <Text style={styles.attemptDetailTitle}>Best attempt details</Text>
                              <Text style={styles.attemptDetailMeta}>
                                Saved on{' '}
                                {formatDateTime(
                                  student.best_attempt.uploaded_at || student.best_attempt.created_at
                                )}
                              </Text>
                            </View>
                            <MaterialIcons
                              name={expandedAttemptId === student.best_attempt.id ? 'expand-less' : 'expand-more'}
                              size={22}
                              color="#385742"
                            />
                          </View>

                          {expandedAttemptId === student.best_attempt.id ? (
                            <View style={styles.attemptQuestionsList}>
                              {student.best_attempt.answers.map((answer) => {
                                const question = questionByNumber.get(answer.question_number) ?? null;
                                const markedOption = answer.final_option ?? answer.detected_option ?? '-';
                                const correctOption = answer.correct_option ?? '-';

                                return (
                                  <View key={answer.id} style={styles.attemptQuestionRow}>
                                    <View style={styles.attemptQuestionTop}>
                                      <Text style={styles.attemptQuestionNumber}>Q{answer.question_number}</Text>
                                      <Text style={styles.attemptQuestionStatus}>
                                        {getAnswerStatusLabel(answer)}
                                      </Text>
                                    </View>
                                    <Text style={styles.attemptQuestionPrompt}>
                                      {question?.prompt ?? `Question ${answer.question_number}`}
                                    </Text>
                                    <View style={styles.attemptAnswerStrip}>
                                      <View style={styles.answerChip}>
                                        <Text style={styles.answerChipValue}>{String(markedOption)}</Text>
                                        <Text style={styles.answerChipLabel}>marked</Text>
                                      </View>
                                      <View style={styles.answerChip}>
                                        <Text style={styles.answerChipValue}>{String(correctOption)}</Text>
                                        <Text style={styles.answerChipLabel}>correct</Text>
                                      </View>
                                    </View>
                                  </View>
                                );
                              })}
                            </View>
                          ) : null}
                        </Pressable>

                        {student.other_attempts.length > 0 ? (
                          <View style={styles.subSection}>
                            <Text style={styles.subSectionTitle}>Remaining attempts</Text>
                            <View style={styles.otherAttemptsList}>
                              {student.other_attempts.map((attempt, attemptIndex) => (
                                <Pressable
                                  key={attempt.id}
                                  onPress={() =>
                                    setExpandedAttemptId((current) =>
                                      current === attempt.id ? null : attempt.id
                                    )
                                  }
                                  style={styles.otherAttemptRow}>
                                  <View style={styles.attemptDetailHeader}>
                                    <View style={styles.attemptDetailCopy}>
                                      <Text style={styles.otherAttemptIndex}>Attempt {attemptIndex + 2}</Text>
                                      <Text style={styles.otherAttemptMeta}>
                                        {attempt.percent}% | {attempt.score}/{attempt.total_marks} |{' '}
                                        {formatDateTime(attempt.uploaded_at || attempt.created_at)}
                                      </Text>
                                    </View>
                                    <MaterialIcons
                                      name={expandedAttemptId === attempt.id ? 'expand-less' : 'expand-more'}
                                      size={22}
                                      color="#5E6E61"
                                    />
                                  </View>

                                  {expandedAttemptId === attempt.id ? (
                                    <View style={styles.attemptQuestionsList}>
                                      {attempt.answers.map((answer) => {
                                        const question = questionByNumber.get(answer.question_number) ?? null;
                                        const markedOption = answer.final_option ?? answer.detected_option ?? '-';
                                        const correctOption = answer.correct_option ?? '-';

                                        return (
                                          <View key={answer.id} style={styles.attemptQuestionRow}>
                                            <View style={styles.attemptQuestionTop}>
                                              <Text style={styles.attemptQuestionNumber}>
                                                Q{answer.question_number}
                                              </Text>
                                              <Text style={styles.attemptQuestionStatus}>
                                                {getAnswerStatusLabel(answer)}
                                              </Text>
                                            </View>
                                            <Text style={styles.attemptQuestionPrompt}>
                                              {question?.prompt ?? `Question ${answer.question_number}`}
                                            </Text>
                                            <View style={styles.attemptAnswerStrip}>
                                              <View style={styles.answerChip}>
                                                <Text style={styles.answerChipValue}>
                                                  {String(markedOption)}
                                                </Text>
                                                <Text style={styles.answerChipLabel}>marked</Text>
                                              </View>
                                              <View style={styles.answerChip}>
                                                <Text style={styles.answerChipValue}>
                                                  {String(correctOption)}
                                                </Text>
                                                <Text style={styles.answerChipLabel}>correct</Text>
                                              </View>
                                            </View>
                                          </View>
                                        );
                                      })}
                                    </View>
                                  ) : null}
                                </Pressable>
                              ))}
                            </View>
                          </View>
                        ) : null}
                      </View>
                    ))}
                  </View>
                ) : questionLoading ? (
                  <View style={styles.questionLoadingState}>
                    <ActivityIndicator color="#1E5E3D" />
                    <Text style={styles.questionLoadingText}>Loading question details...</Text>
                  </View>
                ) : (
                  <View style={styles.analyticsList}>
                    {questionAnalytics.map((question) => {
                      const isExpanded = expandedQuestionNumber === question.question_number;
                      const hasDetails = Boolean(question.question);
                      const questionDetail = question.question;

                      return (
                        <Pressable
                          key={question.question_number}
                          onPress={() =>
                            setExpandedQuestionNumber((current) =>
                              current === question.question_number ? null : question.question_number
                            )
                          }
                          style={[styles.analyticsCard, styles.questionCard]}>
                          <View style={styles.questionCardTop}>
                            <View style={styles.questionBadge}>
                              <Text style={styles.questionBadgeText}>Q{question.question_number}</Text>
                            </View>

                            <View style={styles.questionTopCopy}>
                              <Text style={styles.analyticsCardTitle}>
                                {hasDetails ? questionDetail?.prompt : `Question ${question.question_number}`}
                              </Text>
                              <Text style={styles.analyticsCardMeta}>
                                {question.attempts} attempts | {question.correct} correct | {question.wrong} wrong
                              </Text>
                            </View>

                            <MaterialIcons
                              name={isExpanded ? 'expand-less' : 'expand-more'}
                              size={22}
                              color="#49614E"
                            />
                          </View>

                          <View style={styles.metricRow}>
                            <View style={styles.metricPill}>
                              <Text style={styles.metricValue}>{question.attempts}</Text>
                              <Text style={styles.metricLabel}>attempts</Text>
                            </View>
                            <View style={styles.metricPill}>
                              <Text style={styles.metricValue}>{question.correct}</Text>
                              <Text style={styles.metricLabel}>correct</Text>
                            </View>
                            <View style={styles.metricPill}>
                              <Text style={styles.metricValue}>{question.wrong}</Text>
                              <Text style={styles.metricLabel}>wrong</Text>
                            </View>
                            <View style={styles.metricPill}>
                              <Text style={styles.metricValue}>{question.blank}</Text>
                              <Text style={styles.metricLabel}>blank</Text>
                            </View>
                            <View style={styles.metricPill}>
                              <Text style={styles.metricValue}>{question.review}</Text>
                              <Text style={styles.metricLabel}>review</Text>
                            </View>
                          </View>

                          {isExpanded && questionDetail ? (
                            <View style={styles.questionExpanded}>
                              <Text style={styles.questionPrompt}>{questionDetail.prompt}</Text>

                              <View style={styles.optionList}>
                                {(['A', 'B', 'C', 'D'] as OptionKey[]).map((option) => {
                                  const isCorrect = questionDetail.correct_option === option;
                                  return (
                                    <View
                                      key={option}
                                      style={[styles.optionRow, isCorrect && styles.optionRowCorrect]}>
                                      <View style={styles.optionLead}>
                                        <View
                                          style={[styles.optionBadge, isCorrect && styles.optionBadgeCorrect]}>
                                          <Text
                                            style={[
                                              styles.optionBadgeText,
                                              isCorrect && styles.optionBadgeTextCorrect,
                                            ]}>
                                            {option}
                                          </Text>
                                        </View>
                                        <Text
                                          style={[
                                            styles.optionText,
                                            isCorrect && styles.optionTextCorrect,
                                          ]}>
                                          {getOptionText(questionDetail, option)}
                                        </Text>
                                      </View>

                                      <View style={styles.optionCountPill}>
                                        <Text style={styles.optionCountValue}>
                                          {question.marked_counts[option]}
                                        </Text>
                                        <Text style={styles.optionCountLabel}>marked</Text>
                                      </View>
                                    </View>
                                  );
                                })}
                              </View>

                              {questionDetail.explanation?.trim() ? (
                                <View style={styles.explanationBox}>
                                  <Text style={styles.explanationLabel}>Explanation</Text>
                                  <Text style={styles.explanationText}>{questionDetail.explanation}</Text>
                                </View>
                              ) : null}
                            </View>
                          ) : null}
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>
            ) : null}
          </>
        )}
      </ScrollView>

      <Modal
        transparent
        visible={pickerType !== null}
        animationType="slide"
        onRequestClose={() => setPickerType(null)}>
        <View style={styles.pickerOverlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setPickerType(null)} />
          <View style={styles.pickerCard}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>{pickerTitle}</Text>
              <Pressable onPress={() => setPickerType(null)} style={styles.pickerClose}>
                <MaterialIcons name="close" size={20} color="#5F6C60" />
              </Pressable>
            </View>

            <ScrollView contentContainerStyle={styles.pickerList} showsVerticalScrollIndicator={false}>
              {pickerType === 'class' &&
                classOptions.map((option) => {
                  const active = selectedClassId === option.id;
                  return (
                    <Pressable
                      key={option.id}
                      onPress={() => {
                        setSelectedClassId(option.id);
                        setSelectedSetId('');
                        setSelectedQuestions([]);
                        setAnalyticsView('student');
                        setExpandedAttemptId(null);
                        setExpandedQuestionNumber(null);
                        setPickerType(null);
                      }}
                      style={[styles.pickerItem, active && styles.pickerItemActive]}>
                      <View style={styles.pickerItemCopy}>
                        <Text style={[styles.pickerName, active && styles.pickerNameActive]}>{option.label}</Text>
                        <Text style={styles.pickerMeta}>Filter MCQ sets for this class</Text>
                      </View>
                      <MaterialIcons
                        name={active ? 'check-circle' : 'radio-button-unchecked'}
                        size={20}
                        color={active ? '#1B5E20' : '#AAB7AC'}
                      />
                    </Pressable>
                  );
                })}

              {pickerType === 'set' &&
                visibleSets.map((set) => {
                  const active = selectedSetId === set.id;
                  return (
                    <Pressable
                      key={set.id}
                      onPress={() => {
                        setSelectedSetId(set.id);
                        setAnalyticsView('student');
                        setExpandedAttemptId(null);
                        setExpandedQuestionNumber(null);
                        setPickerType(null);
                      }}
                      style={[styles.pickerItem, active && styles.pickerItemActive]}>
                      <View style={styles.pickerItemCopy}>
                        <Text style={[styles.pickerName, active && styles.pickerNameActive]}>{set.title}</Text>
                        <Text style={styles.pickerMeta}>
                          {set.subject_label} | {set.question_count} questions
                        </Text>
                      </View>
                      <MaterialIcons
                        name={active ? 'check-circle' : 'radio-button-unchecked'}
                        size={20}
                        color={active ? '#1B5E20' : '#AAB7AC'}
                      />
                    </Pressable>
                  );
                })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function PickerField({
  disabled = false,
  icon,
  label,
  onPress,
  value,
}: {
  disabled?: boolean;
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  onPress: () => void;
  value: string;
}) {
  const isPlaceholder = value.startsWith('Select');

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[styles.pickerField, disabled && styles.pickerFieldDisabled]}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={[styles.pickerFieldBox, disabled && styles.pickerFieldBoxDisabled]}>
        <View style={styles.pickerFieldLeft}>
          <MaterialIcons name={icon} size={18} color={disabled ? '#9BA89C' : '#1B5E20'} />
          <Text
            style={[
              styles.pickerFieldValue,
              isPlaceholder && styles.pickerFieldPlaceholder,
              disabled && styles.pickerFieldPlaceholder,
            ]}>
            {disabled ? 'No MCQ sets available' : value}
          </Text>
        </View>
        <MaterialIcons name="unfold-more" size={20} color={disabled ? '#D1D8D1' : '#C7D5C9'} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  content: {
    padding: 20,
    paddingBottom: 36,
    gap: 18,
  },
  contentDesktop: {
    paddingHorizontal: 28,
    paddingTop: 24,
    gap: 20,
  },
  loadingScreen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: '#F5F1E8',
    padding: 24,
  },
  loadingText: {
    color: '#536056',
    fontSize: 14,
    fontWeight: '600',
  },
  hero: {
    backgroundColor: '#1E5E3D',
    borderRadius: 28,
    padding: 22,
    gap: 14,
  },
  heroEyebrow: {
    color: '#D5E8DA',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroSubtitle: {
    color: '#DDEADF',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 760,
  },
  heroStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  heroStatCard: {
    minWidth: 108,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 18,
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 4,
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '800',
  },
  heroStatLabel: {
    color: '#D5E8DA',
    fontSize: 12,
    fontWeight: '600',
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    padding: 18,
    gap: 14,
    borderWidth: 1,
    borderColor: '#E7E2D8',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 19,
    fontWeight: '800',
    color: '#183224',
    fontFamily: Fonts.rounded,
  },
  sectionCaption: {
    fontSize: 13,
    color: '#6A766B',
    fontWeight: '600',
    marginTop: 3,
  },
  selectorStack: {
    gap: 14,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '800',
    color: '#48624F',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  pickerField: {
    gap: 8,
  },
  pickerFieldDisabled: {
    opacity: 0.72,
  },
  pickerFieldBox: {
    minHeight: 56,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerFieldBoxDisabled: {
    backgroundColor: '#F4F6F4',
  },
  pickerFieldLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  pickerFieldValue: {
    color: '#1C1C1E',
    fontSize: 15,
    flexShrink: 1,
  },
  pickerFieldPlaceholder: {
    color: '#A8B9AA',
  },
  selectionCard: {
    borderRadius: 20,
    backgroundColor: '#F7F4EC',
    borderWidth: 1,
    borderColor: '#E8E1D2',
    padding: 16,
    gap: 8,
  },
  selectionLabel: {
    fontSize: 12,
    color: '#6B756B',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  selectionTitle: {
    fontSize: 20,
    lineHeight: 25,
    color: '#183224',
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  selectionMeta: {
    fontSize: 13,
    color: '#627062',
    lineHeight: 18,
  },
  selectionStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 4,
  },
  selectionStatPill: {
    minWidth: 110,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 2,
    borderWidth: 1,
    borderColor: '#E8E1D2',
  },
  selectionStatValue: {
    fontSize: 14,
    color: '#173623',
    fontWeight: '800',
  },
  selectionStatLabel: {
    fontSize: 11,
    color: '#718073',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  analyticsHero: {
    borderRadius: 22,
    backgroundColor: '#F3F7F3',
    borderWidth: 1,
    borderColor: '#E0E9E0',
    padding: 16,
    gap: 14,
  },
  analyticsHeroCopy: {
    gap: 6,
  },
  analyticsEyebrow: {
    fontSize: 12,
    color: '#4F6B58',
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  analyticsTitle: {
    fontSize: 22,
    color: '#173723',
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  analyticsSubtitle: {
    fontSize: 13,
    lineHeight: 19,
    color: '#607062',
  },
  analyticsMiniStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  analyticsMiniStat: {
    minWidth: 100,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E0E9E0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 2,
  },
  analyticsMiniValue: {
    fontSize: 16,
    color: '#183224',
    fontWeight: '800',
  },
  analyticsMiniLabel: {
    fontSize: 11,
    color: '#738176',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  analyticsToggleShell: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  analyticsToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 16,
    backgroundColor: '#EEF2EB',
  },
  analyticsToggleButtonActive: {
    backgroundColor: '#1E5E3D',
  },
  analyticsToggleText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#215136',
  },
  analyticsToggleTextActive: {
    color: '#FFFFFF',
  },
  analyticsList: {
    gap: 12,
  },
  analyticsCard: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E6E2D8',
    backgroundColor: '#FBFAF7',
    padding: 16,
    gap: 12,
  },
  analyticsCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  analyticsRankBadge: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E7F0E7',
  },
  analyticsRankText: {
    fontSize: 13,
    fontWeight: '900',
    color: '#1E5E3D',
  },
  analyticsHeaderCopy: {
    flex: 1,
    gap: 4,
  },
  analyticsCardTitle: {
    fontSize: 18,
    color: '#183224',
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  analyticsCardMeta: {
    fontSize: 13,
    color: '#667266',
    lineHeight: 18,
  },
  scoreBadge: {
    minWidth: 74,
    borderRadius: 16,
    backgroundColor: '#1E5E3D',
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignItems: 'center',
    gap: 2,
  },
  scoreBadgeValue: {
    fontSize: 18,
    fontWeight: '900',
    color: '#FFFFFF',
  },
  scoreBadgeLabel: {
    fontSize: 10,
    color: '#D6E9DA',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  metricRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  metricPill: {
    minWidth: 86,
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E7E2D8',
    gap: 3,
  },
  metricValue: {
    fontSize: 15,
    color: '#193425',
    fontWeight: '800',
  },
  metricLabel: {
    fontSize: 11,
    color: '#718073',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  attemptDetailCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 12,
    gap: 10,
  },
  attemptDetailCardPrimary: {
    borderColor: '#DDE9DF',
    backgroundColor: '#F4F8F4',
  },
  attemptDetailHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  attemptDetailCopy: {
    flex: 1,
    gap: 3,
  },
  attemptDetailTitle: {
    fontSize: 13,
    color: '#2F4C39',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  attemptDetailMeta: {
    fontSize: 12,
    color: '#647466',
    lineHeight: 17,
  },
  attemptQuestionsList: {
    gap: 8,
  },
  attemptQuestionRow: {
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E3E8DF',
    padding: 12,
    gap: 8,
  },
  attemptQuestionTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  attemptQuestionNumber: {
    fontSize: 12,
    fontWeight: '900',
    color: '#21442D',
  },
  attemptQuestionStatus: {
    fontSize: 11,
    color: '#6B786D',
    fontWeight: '700',
  },
  attemptQuestionPrompt: {
    fontSize: 13,
    lineHeight: 18,
    color: '#223327',
  },
  attemptAnswerStrip: {
    flexDirection: 'row',
    gap: 8,
  },
  answerChip: {
    minWidth: 68,
    borderRadius: 12,
    backgroundColor: '#F3F5F1',
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 1,
    alignItems: 'center',
  },
  answerChipValue: {
    fontSize: 14,
    fontWeight: '900',
    color: '#1F3928',
  },
  answerChipLabel: {
    fontSize: 10,
    color: '#718073',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  subSection: {
    gap: 8,
  },
  subSectionTitle: {
    fontSize: 13,
    color: '#3A5643',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  otherAttemptsList: {
    gap: 8,
  },
  otherAttemptRow: {
    borderRadius: 14,
    backgroundColor: '#F2F4EF',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  otherAttemptIndex: {
    fontSize: 12,
    color: '#284334',
    fontWeight: '800',
  },
  otherAttemptMeta: {
    fontSize: 12,
    color: '#677567',
    lineHeight: 17,
  },
  questionLoadingState: {
    borderRadius: 18,
    backgroundColor: '#F8F6F0',
    padding: 18,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  questionLoadingText: {
    fontSize: 13,
    color: '#61705F',
    fontWeight: '600',
  },
  questionCard: {
    gap: 14,
  },
  questionCardTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  questionBadge: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 9,
    backgroundColor: '#E8F0E8',
  },
  questionBadgeText: {
    fontSize: 12,
    fontWeight: '900',
    color: '#1E5E3D',
  },
  questionTopCopy: {
    flex: 1,
    gap: 4,
  },
  questionExpanded: {
    gap: 12,
    paddingTop: 2,
  },
  questionPrompt: {
    fontSize: 14,
    lineHeight: 21,
    color: '#1B2E22',
    fontWeight: '600',
  },
  optionList: {
    gap: 10,
  },
  optionRow: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5E8E0',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  optionRowCorrect: {
    borderColor: '#7DB78A',
    backgroundColor: '#EDF8EF',
  },
  optionLead: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  optionBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EEF2EB',
  },
  optionBadgeCorrect: {
    backgroundColor: '#1E5E3D',
  },
  optionBadgeText: {
    fontSize: 13,
    fontWeight: '900',
    color: '#35503D',
  },
  optionBadgeTextCorrect: {
    color: '#FFFFFF',
  },
  optionText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: '#223327',
  },
  optionTextCorrect: {
    color: '#173723',
    fontWeight: '700',
  },
  optionCountPill: {
    minWidth: 64,
    borderRadius: 14,
    backgroundColor: '#F4F6F1',
    paddingHorizontal: 10,
    paddingVertical: 8,
    alignItems: 'center',
    gap: 1,
  },
  optionCountValue: {
    fontSize: 15,
    fontWeight: '900',
    color: '#1F3928',
  },
  optionCountLabel: {
    fontSize: 10,
    color: '#708072',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  explanationBox: {
    borderRadius: 16,
    backgroundColor: '#F4F1E8',
    padding: 12,
    gap: 4,
  },
  explanationLabel: {
    fontSize: 12,
    color: '#5C6B60',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  explanationText: {
    fontSize: 13,
    lineHeight: 19,
    color: '#314235',
  },
  pickerOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    justifyContent: 'flex-end',
  },
  pickerCard: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    padding: 18,
    gap: 14,
    maxHeight: '72%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerTitle: {
    color: '#173D22',
    fontSize: 18,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  pickerClose: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F3F5F2',
  },
  pickerList: {
    gap: 10,
    paddingBottom: 12,
  },
  pickerItem: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5EEE5',
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  pickerItemActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#1B5E20',
  },
  pickerItemCopy: {
    flex: 1,
    gap: 4,
  },
  pickerName: {
    color: '#173D22',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  pickerNameActive: {
    color: '#1B5E20',
  },
  pickerMeta: {
    color: '#6A8570',
    fontSize: 12,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 36,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#E7E2D8',
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#183224',
  },
  emptyText: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    color: '#6B776C',
    maxWidth: 460,
  },
  compactEmptyState: {
    borderRadius: 18,
    padding: 16,
    backgroundColor: '#F8F6F0',
    gap: 6,
  },
  compactEmptyTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#23392B',
  },
  compactEmptyText: {
    fontSize: 13,
    lineHeight: 19,
    color: '#687468',
  },
});
